import { Controller } from "@hotwired/stimulus";
import offlineProgressSync from "../../../../src/react/helpers/OfflineProgressSync";
import { seemsOffline, visit } from "../../../../utils/connectivity";
import { isPwaEnabled } from "../../../../utils/offline_gating";

const ASSESSMENT_PENDING_NEXT_URL_KEY = "assessmentOfflinePendingNextUrl";
const FROZEN_TIMER_KEY = "assessmentOfflineFrozenTimer";

const CERTAINTY_LABELS = {
  very_certain: "Very Certain",
  certain: "Certain",
  uncertain: "Uncertain",
  very_uncertain: "Very Uncertain",
};

const answerFrom = (payload) => ({
  answerIndex: String(payload["assessment_item_response[answer_index]"]),
  struckIndexes: payload["indexes[]"] ?? [],
  confidence: payload["assessment_item_response[confidence]"],
});

export default class extends Controller {
  static targets = [
    "form",
    "revealTemplate",
    "questionRoot",
    "offlineContinueButton",
    "offlineSavedMessage",
    "continueStatusAnnouncement",
  ];

  static outlets = ["offline-navigation"];

  static values = {
    rosteringId: Number,
    itemIndex: Number,
    mode: String,
    nextQuestionUrl: String,
    offlineContinueUrl: String,
    correctAnswerIndex: Number,
    answerLetter: String,
    autoAddTodos: Boolean,
  };

  connect() {
    this.startTime = Date.now();
    this.pwaEnabled = isPwaEnabled();
    this.sawOffline = !navigator.onLine;
    this.boundUpdateOfflineBanner = this.updateOfflineBanner.bind(this);
    this.boundHandleBlockedAction = this.handleBlockedAction.bind(this);
    this.updateOfflineBanner();
    window.addEventListener("online", this.boundUpdateOfflineBanner);
    window.addEventListener("offline", this.boundUpdateOfflineBanner);
    document.addEventListener("click", this.boundHandleBlockedAction, true);
  }

  disconnect() {
    if (this.boundUpdateOfflineBanner) {
      window.removeEventListener("online", this.boundUpdateOfflineBanner);
      window.removeEventListener("offline", this.boundUpdateOfflineBanner);
    }
    if (this.boundHandleBlockedAction) {
      document.removeEventListener(
        "click",
        this.boundHandleBlockedAction,
        true,
      );
    }
  }

  handleBlockedAction(event) {
    if (!this.pwaEnabled || !seemsOffline()) return;

    const clickable = event.target.closest(
      "a, button, .calibrate-pause-trigger, .player-support-link, .add-todo-button, .todo-manage-button, .delete-auto-todo-link, .next-question",
    );
    if (!clickable || !this.isOfflineBlockedAction(clickable)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    if (this.hasOfflineNavigationOutlet) {
      this.offlineNavigationOutlet.showFeatureUnavailable(clickable);
    } else {
      this.showFlash(
        "This action needs an internet connection. Please reconnect, then try again.",
      );
    }
  }

  isOfflineBlockedAction(element) {
    if (element.closest(".calibrate-pause-trigger")) return true;
    if (element.closest(".player-support-link")) return true;
    if (
      element.closest(
        ".add-todo-button, .todo-manage-button, .delete-auto-todo-link",
      )
    ) {
      return true;
    }

    if (element.closest(".next-question")) {
      const continueButton = this.hasOfflineContinueButtonTarget
        ? this.offlineContinueButtonTarget
        : null;
      if (continueButton && element.closest("button, a") === continueButton) {
        return false;
      }
      return true;
    }

    const action = element.getAttribute("data-action") || "";
    return (
      action.includes("to-do-modal#openTodoModal") ||
      action.includes("saq#openTodoModal") ||
      action.includes("content-library-modal#openTodoModal") ||
      action.includes("text-decoration#openTodoModal") ||
      action.includes("aquifer-ui-quiz-results-todo-modal#openTodoModal")
    );
  }

  updateOfflineBanner() {
    const isOnline = navigator.onLine;
    const banner = document.getElementById("offline-indicator");
    if (banner) {
      banner.classList.toggle("hidden", isOnline);
    }
    const syncStatus = document.getElementById("sync-status");
    if (syncStatus) {
      syncStatus.classList.toggle("hidden", isOnline);
    }
    this.holdExamClockWhileWaiting(isOnline);
  }

  holdExamClockWhileWaiting(isOnline) {
    if (!this.pwaEnabled) return;

    if (!isOnline) {
      this.sawOffline = true;
      if (this.stillAnswering()) return;
      this.freezeExamTimer();
      return;
    }

    if (!this.sawOffline || window.location.pathname.endsWith("/edit")) {
      this.sawOffline = false;
      return;
    }

    this.sawOffline = false;
    offlineProgressSync.creditFrozenExamTime?.(this.rosteringIdValue);
  }

  stillAnswering() {
    return window.location.pathname.endsWith("/edit") && !this.revealRendered;
  }

  setResponseTime() {
    if (!this.hasFormTarget) return;
    const input = this.formTarget.querySelector("#responseTime");
    if (input) {
      input.value = Date.now() - (this.startTime || Date.now());
    }
  }

  serializeForm() {
    const payload = {};
    if (!this.hasFormTarget) return payload;

    const formData = new FormData(this.formTarget);
    formData.forEach((value, key) => {
      if (key.endsWith("[]")) {
        if (!payload[key]) payload[key] = [];
        payload[key].push(value);
      } else if (payload[key]) {
        payload[key] = Array.isArray(payload[key])
          ? [...payload[key], value]
          : [payload[key], value];
      } else {
        payload[key] = value;
      }
    });

    return payload;
  }

  async handleSubmit(event) {
    this.setResponseTime();

    if (!this.pwaEnabled) return;
    const offlineSync = offlineProgressSync;
    if (!offlineSync) return;

    const shouldHandleOffline = offlineSync.shouldQueue() || !navigator.onLine;
    if (!shouldHandleOffline) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation?.();

    try {
      const payload = this.serializeForm();
      const continueUrl = this.continueUrl();
      await offlineSync.queueAssessmentItemResponse({
        rosteringId: this.rosteringIdValue,
        itemIndex: this.itemIndexValue,
        mode: this.modeValue,
        formData: payload,
        nextQuestionUrl: this.nextQuestionUrlValue || "",
        currentPath: window.location.pathname,
      });
      window.dispatchEvent(new CustomEvent("assessment-offline:queued"));
      this.freezeExamTimer();

      if (continueUrl) {
        localStorage.setItem(ASSESSMENT_PENDING_NEXT_URL_KEY, continueUrl);
      }

      this.disableSubmitControls();
      this.revealFromPayload(payload);
    } catch (error) {
      console.error("[CalibrateOffline] Failed to queue response", error);
      this.showFlash(
        "Unable to save your answer offline. Please reconnect and try again.",
      );
    }

    return false;
  }

  async handleOfflineContinue(event) {
    const continueUrl = this.continueUrl();
    if (!continueUrl) return;

    if (seemsOffline()) {
      event?.preventDefault();
      localStorage.setItem(ASSESSMENT_PENDING_NEXT_URL_KEY, continueUrl);
      if (this.hasOfflineNavigationOutlet) {
        this.offlineNavigationOutlet.showFeatureUnavailable(
          event?.currentTarget ?? null,
        );
      } else {
        this.showFlash(
          "This action needs an internet connection. Please reconnect, then try again.",
        );
      }
      return;
    }

    event?.preventDefault();
    const offlineSync = offlineProgressSync;
    if (!offlineSync?.waitForAssessmentItemResponseSynced) return;

    this.setContinueButtonBusy(true);
    this.announceContinueStatus("Saving your answer...");

    try {
      const result = await offlineSync.waitForAssessmentItemResponseSynced(
        this.rosteringIdValue,
        this.itemIndexValue,
      );
      if (!result.ok) {
        const message =
          result.message || "Unable to save your answer. Please try again.";
        this.announceContinueStatus(message);
        this.showFlash(message);
        if (result.failed && offlineSync.retryFailed) {
          await offlineSync.retryFailed().catch(() => {});
        }
        return;
      }

      this.announceContinueStatus("Answer saved.");
      const showPath = `${window.location.pathname.replace(/\/edit$/, "")}${window.location.search}`;
      visit(showPath);
    } finally {
      this.setContinueButtonBusy(false);
    }
  }

  continueUrl() {
    return this.offlineContinueUrlValue || this.nextQuestionUrlValue || "";
  }

  revealFromPayload(payload) {
    if (!this.hasRevealTemplateTarget) return;

    const reveal = this.revealTemplateTarget.content.cloneNode(true);
    const { answerIndex, struckIndexes, confidence } = answerFrom(payload);
    const correct = this.isCorrect(payload);

    reveal.querySelectorAll("[data-choice-index]").forEach((row) => {
      const index = String(row.dataset.choiceIndex);
      const chosen = index === answerIndex;
      const radio = row.querySelector('input[type="radio"]');
      if (radio) radio.checked = chosen;
      row
        .querySelector(".player-option-label")
        ?.classList.toggle("line-through", struckIndexes.includes(index));
      row.querySelectorAll("[data-reveal-certainty]").forEach((pill) => {
        if (!chosen || pill.dataset.revealCertainty !== confidence)
          pill.remove();
      });
      row.querySelectorAll("[data-reveal-choice-correct]").forEach((icon) => {
        const marksCorrectChoice = icon.dataset.revealChoiceCorrect === "true";
        if (
          marksCorrectChoice &&
          index !== String(this.correctAnswerIndexValue)
        ) {
          icon.remove();
          return;
        }
        if (!marksCorrectChoice && !(chosen && !correct)) {
          icon.remove();
        }
      });
    });

    reveal.querySelectorAll("[data-reveal-correct]").forEach((icon) => {
      if (icon.dataset.revealCorrect !== String(correct)) icon.remove();
    });

    this.appendAutoAddTodoMessage(reveal, payload, correct);
    this.renderOfflineCccBadge(reveal, payload, correct);

    const root =
      (this.hasQuestionRootTarget && this.questionRootTarget) ||
      this.element.querySelector(".player-question-body") ||
      this.element;
    root.replaceChildren(reveal);
    this.revealRendered = true;
  }

  appendAutoAddTodoMessage(reveal, payload, correct) {
    if (!this.autoAddTodosValue || correct) return;

    const savedMessage = reveal.querySelector(
      "[data-quiz-player--calibrate-offline-target='offlineSavedMessage']",
    );
    if (!savedMessage) return;

    const note = document.createElement("p");
    note.className = "type-body mt-2";
    note.textContent =
      "Related case will be added to your To-do list when you reconnect.";
    savedMessage.insertAdjacentElement("afterend", note);
  }

  isCorrect(payload) {
    return (
      Number(answerFrom(payload).answerIndex) === this.correctAnswerIndexValue
    );
  }

  renderOfflineCccBadge(reveal, payload, correct) {
    const slot = reveal.querySelector(
      ".flex.flex-wrap.items-center.justify-between",
    );
    if (!slot) return;

    const { confidence } = answerFrom(payload);
    const certainty =
      CERTAINTY_LABELS[confidence] ||
      String(confidence || "")
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
    const summary = `${correct ? "Correct" : "Wrong"} & ${certainty}`;
    // ponytail: offline pill approximates accuracy + certainty; full CCC (incl. efficiency) is server-only on /show.
    const tone = correct ? "green" : "red";
    const badge = document.createElement("span");
    badge.className = `badge-aq-chip badge-aq-chip--outline badge-aq-chip--outline-${tone} h-6 shrink-0`;
    badge.setAttribute("aria-label", summary);
    badge.innerHTML = `<span>${summary}</span>`;
    slot.appendChild(badge);
    this.announceContinueStatus(summary);
  }

  setContinueButtonBusy(busy) {
    if (!this.hasOfflineContinueButtonTarget) return;
    const button = this.offlineContinueButtonTarget;
    button.disabled = busy;
    button.setAttribute("aria-disabled", busy ? "true" : "false");
    button.setAttribute("aria-busy", busy ? "true" : "false");
  }

  announceContinueStatus(message) {
    if (!this.hasContinueStatusAnnouncementTarget) return;
    this.continueStatusAnnouncementTarget.textContent = message;
  }

  disableSubmitControls() {
    if (!this.hasFormTarget) return;
    this.formTarget
      .querySelectorAll('button[type="submit"], input[type="submit"]')
      .forEach((btn) => {
        btn.setAttribute("disabled", "disabled");
      });
  }

  showFlash(message) {
    const flash = document.getElementById("calibrate-player-flash-messages");
    if (!flash) return;
    flash.textContent = message;
    flash.classList.remove("hidden");
  }

  freezeExamTimer() {
    const playerRoot = document.getElementById("quiz-player");
    if (!playerRoot) return;

    const timerController =
      this.application.getControllerForElementAndIdentifier(
        playerRoot,
        "quiz-player--calibrate-timer",
      );
    if (
      !timerController?.freezeExamClock ||
      timerController.examClockFrozen ||
      timerController.pauseCountdownHandle
    ) {
      return;
    }

    const remainingSeconds = timerController.freezeExamClock();
    try {
      localStorage.setItem(
        FROZEN_TIMER_KEY,
        JSON.stringify({
          rosteringId: this.rosteringIdValue,
          remainingSeconds,
          savedAt: Date.now(),
          frozen: true,
        }),
      );
    } catch (_error) {}
  }
}
